import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';

import { ThemeProvider } from './lib/ThemeContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { ConversionEngineProvider } from './components/ConversionEngineProvider';

import Home from './pages/Home';
import AcilYolYardim from './pages/AcilYolYardim';
import Garanti from './pages/Garanti';
import Markalar from './pages/Markalar';
import BlogPage from './pages/BlogPage';
import BlogPost from './pages/BlogPost';
import Iletisim from './pages/Iletisim';
import { Hakkimizda, Vizyonumuz, Misyonumuz } from './pages/Corporate';
import DistrictPage from './pages/DistrictPage';
import Hizmetlerimiz from './pages/Hizmetlerimiz';
import KocaeliLastikci from './pages/KocaeliLastikci';
import ServiceDetailPage from './pages/ServiceDetailPage';
import NeighborhoodPage from './pages/NeighborhoodPage';
import RoadPage from './pages/RoadPage';
import BrandPage from './pages/BrandPage';
import HizmetSuresi from './pages/HizmetSuresi';
import NotFound from './pages/NotFound';
import { MOBILE_DISTRICT_IDS, ALL_DISTRICT_SLUGS } from './data/districts';
import { ALL_SERVICE_SLUGS } from './data/services-seo';
import { ALL_NEIGHBORHOOD_SLUGS } from './data/neighborhoods';
import { ALL_ROAD_SLUGS } from './data/roads';
import { ALL_BRAND_SLUGS } from './data/brands-seo';

const MOBILE_REDIRECTS = {
  'acil-lastik-yol-yardim': '/acil-yol-yardim',
  'garanti': '/garanti',
  'referanslar': '/markalarimiz',
  'iletisim': '/iletisim',
  'konum-gonder': '/iletisim',
  'hakkimizda': '/hakkimizda',
  'vizyonumuz': '/vizyonumuz',
  'misyonumuz': '/misyonumuz',
  'blog': '/blog',
  'haberler': '/blog',
};

const MOBILE_BLOG_IDS = {
  '1': '/blog/kocaeli-lastik-yol-yardim-hizmeti-nedir',
  '2': '/blog/hizli-lastik-degisimi-mobil-servis',
  '3': '/blog/uzun-sure-beklemeye-son-hizli-mudahale',
  '6': '/blog/724-lastik-yeni-web-arayuzu',
  '7': '/blog/lastik-basinci-kontrolu-neden-onemli',
  '8': '/blog/yaz-aylarinda-lastik-kullanimi-rehberi',
  '9': '/blog/arac-lastik-bakimi-rehberi',
};

// www → non-www client-side fallback redirect
function WwwRedirect() {
  React.useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hostname === 'www.724lastik.com.tr') {
      const target = 'https://724lastik.com.tr' + window.location.pathname + window.location.search + window.location.hash;
      window.location.replace(target);
    }
  }, []);
  return null;
}

function ScrollToTop() {
  const { pathname, search } = require('react-router-dom').useLocation();
  const navigate = require('react-router-dom').useNavigate();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // Handle old mobile URL redirects: /mobil/?sayfa=X
  React.useEffect(() => {
    if (pathname === '/mobil/' || pathname === '/mobil') {
      const params = new URLSearchParams(search);
      const sayfa = params.get('sayfa');
      const id = params.get('id');

      // District/service detail pages: /mobil/?sayfa=hizmet_detay&id=X
      if (sayfa === 'hizmet_detay' && id && MOBILE_DISTRICT_IDS[id]) {
        navigate(MOBILE_DISTRICT_IDS[id], { replace: true });
        return;
      }

      if (sayfa === 'haber_detay' && id && MOBILE_BLOG_IDS[id]) {
        navigate(MOBILE_BLOG_IDS[id], { replace: true });
        return;
      }
      if (sayfa && MOBILE_REDIRECTS[sayfa]) {
        navigate(MOBILE_REDIRECTS[sayfa], { replace: true });
        return;
      }
      // Default: go home
      navigate('/', { replace: true });
    }
  }, [pathname, search, navigate]);

  return null;
}

/*
 * Old .html blog slug → new slug redirect component
 */
function BlogRedirect() {
  const { slug } = require('react-router-dom').useParams();
  const cleanSlug = slug.replace('.html', '');

  const BLOG_MAP = {
    'kocaelide-lastik-yol-yardim-hizmeti': 'kocaeli-lastik-yol-yardim-hizmeti-nedir',
    'hizli-lastik-degisimi-ile-artik-daha-mutlu-olacaksiniz': 'hizli-lastik-degisimi-mobil-servis',
    'uzun-sure-beklemeye-artik-son': 'uzun-sure-beklemeye-son-hizli-mudahale',
    '724-lastik-yeni-web-arayuzu-ile-karsinizda': '724-lastik-yeni-web-arayuzu',
    'yola-cikmadan-once-lastiklerinizin-basincini-kontrol-etmekte-fayda-var': 'lastik-basinci-kontrolu-neden-onemli',
    'yaz-aylarinda-lastik-kullanimina-dikkat': 'yaz-aylarinda-lastik-kullanimi-rehberi',
    'arac-lastik-bakimi': 'arac-lastik-bakimi-rehberi',
  };

  const newSlug = BLOG_MAP[cleanSlug] || cleanSlug;
  return <Navigate to={`/blog/${newSlug}`} replace />;
}

function App() {
  return (
    <HelmetProvider>
    <ThemeProvider>
      <Router>
        <div className="App" style={{ background: 'var(--bg)', minHeight: '100vh', transition: 'background .3s' }}>
          <WwwRedirect />
          <ScrollToTop />
          <ConversionEngineProvider />
          <Navbar />
          <Routes>
            {/* ─── ACTIVE PAGES ─── */}
            <Route path="/" element={<Home />} />
            <Route path="/acil-yol-yardim" element={<AcilYolYardim />} />
            <Route path="/garanti" element={<Garanti />} />
            <Route path="/markalarimiz" element={<Markalar />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/iletisim" element={<Iletisim />} />
            <Route path="/hakkimizda" element={<Hakkimizda />} />
            <Route path="/vizyonumuz" element={<Vizyonumuz />} />
            <Route path="/misyonumuz" element={<Misyonumuz />} />
            <Route path="/hizmetlerimiz" element={<Hizmetlerimiz />} />
            <Route path="/kocaeli-lastikci" element={<KocaeliLastikci />} />

            <Route path="/hizmet-suresi" element={<HizmetSuresi />} />

            {/* ─── SERVICE DETAIL PAGES (SEO) ─── */}
            {ALL_SERVICE_SLUGS.map(slug => (
              <Route key={`svc-${slug}`} path={`/${slug}`} element={<ServiceDetailPage />} />
            ))}

            {/* ─── NEIGHBORHOOD PAGES (SEO) ─── */}
            {ALL_NEIGHBORHOOD_SLUGS.map(slug => (
              <Route key={`hood-${slug}`} path={`/${slug}`} element={<NeighborhoodPage />} />
            ))}

            {/* ─── ROAD PAGES (SEO) ─── */}
            {ALL_ROAD_SLUGS.map(slug => (
              <Route key={`road-${slug}`} path={`/${slug}`} element={<RoadPage />} />
            ))}

            {/* ─── BRAND PAGES (SEO) ─── */}
            {ALL_BRAND_SLUGS.map(slug => (
              <Route key={`brand-${slug}`} path={`/${slug}`} element={<BrandPage />} />
            ))}

            {/* ─── DISTRICT LANDING PAGES (SEO) ─── */}
            {ALL_DISTRICT_SLUGS.map(slug => (
              <Route key={slug} path={`/${slug}`} element={<DistrictPage />} />
            ))}

            {/* ─── OLD SITE REDIRECTS (SEO 301 preservation) ─── */}
            {/* Page redirects */}
            <Route path="/acil-lastik-yol-yardim.html" element={<Navigate to="/acil-yol-yardim" replace />} />
            <Route path="/garanti.html" element={<Navigate to="/garanti" replace />} />
            <Route path="/markalar.html" element={<Navigate to="/markalarimiz" replace />} />
            <Route path="/blog.html" element={<Navigate to="/blog" replace />} />
            <Route path="/iletisim.html" element={<Navigate to="/iletisim" replace />} />
            <Route path="/kurumsal/hakkimizda.html" element={<Navigate to="/hakkimizda" replace />} />
            <Route path="/kurumsal/vizyonumuz.html" element={<Navigate to="/vizyonumuz" replace />} />
            <Route path="/kurumsal/misyonumuz.html" element={<Navigate to="/misyonumuz" replace />} />
            <Route path="/konum-gonder.html" element={<Navigate to="/iletisim" replace />} />

            {/* Blog post redirects (.html slug → clean slug) */}
            <Route path="/blog/:slug.html" element={<BlogRedirect />} />

            {/* Mobile old URLs — handled by ScrollToTop useEffect */}
            <Route path="/mobil" element={<Home />} />
            <Route path="/mobil/" element={<Home />} />

            {/* Catch-all: 404 Page */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Footer />
        </div>
      </Router>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
